import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { chat } from "@/lib/api";

const getSessionId = () => {
  let sid = localStorage.getItem("woop_chat_sid");
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("woop_chat_sid", sid);
  }
  return sid;
};

const Chatbot = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  // Reset welcome message when language changes
  useEffect(() => {
    setMessages([{ role: "bot", text: t("chatbot.welcome") }]);
  }, [i18n.language, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { reply } = await chat.send(sessionId.current, text, i18n.language);
      setMessages((m) => [...m, { role: "bot", text: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: t("chatbot.errorReply") }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[540px] bg-white border border-zinc-900 shadow-2xl flex flex-col"
          >
            <div className="bg-zinc-950 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#4F46E5] flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm">Koni</div>
                  <div className="text-xs text-zinc-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    {t("chatbot.online")}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chatbot-close-btn"
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
              {messages.map((m, i) => (
                <div
                  key={`${m.role}-${i}-${m.text.slice(0, 12)}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`chat-msg-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#4F46E5] text-white"
                        : "bg-white border border-zinc-200 text-zinc-900"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-zinc-200 px-4 py-3 flex gap-1.5">
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="border-t border-zinc-200 p-3 flex items-center gap-2 bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chatbot.placeholder")}
                data-testid="chatbot-input"
                className="flex-1 px-3 py-2.5 border border-zinc-300 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none text-sm"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="chatbot-send-btn"
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white p-2.5 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chatbot-toggle-btn"
        onClick={() => setOpen(!open)}
        className={`fixed bottom-5 right-4 sm:right-6 z-50 w-14 h-14 bg-[#4F46E5] hover:bg-[#4338CA] text-white flex items-center justify-center shadow-xl transition-all ${
          !open ? "pulse-ring" : ""
        }`}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};

export default Chatbot;
